import { App } from "@capacitor/app"
import { isMiniAppActive, onMiniAppActiveChange } from "@shared/lib/telegram"
import posthog from "posthog-js"
import { detectPlatform } from "./detect"

type Listener = (isActive: boolean) => void

const listeners = new Set<Listener>()
let detach: (() => void) | null = null
let nativeActive: boolean | null = null

function emit(isActive: boolean): void {
	for (const listener of [...listeners]) {
		if (listeners.has(listener)) {
			listener(isActive)
		}
	}
}

function attachVisibility(): () => void {
	const handler = () => {
		emit(document.visibilityState === "visible")
	}
	document.addEventListener("visibilitychange", handler)
	return () => document.removeEventListener("visibilitychange", handler)
}

function attachTelegram(): () => void {
	let last = isAppActive()
	const sync = () => {
		const next = isAppActive()
		if (next === last) return
		last = next
		emit(next)
	}
	const offMiniApp = onMiniAppActiveChange(sync)
	if (offMiniApp === null) {
		posthog.captureException(new Error("miniApp is not mounted"), {
			scope: "platform.appActive",
		})
		return attachVisibility()
	}
	document.addEventListener("visibilitychange", sync)
	return () => {
		offMiniApp()
		document.removeEventListener("visibilitychange", sync)
	}
}

function attachNative(): () => void {
	let cancelled = false
	let remove: (() => void) | null = null

	App.addListener("appStateChange", ({ isActive }) => {
		if (cancelled) return
		nativeActive = isActive
		emit(isActive)
	})
		.then((handle) => {
			remove = () => {
				void handle.remove().catch(() => {})
			}
			if (cancelled) {
				remove()
			}
		})
		.catch((error) => {
			posthog.captureException(error, { scope: "platform.appActive" })
		})

	return () => {
		cancelled = true
		nativeActive = null
		remove?.()
	}
}

function attachPlatform(): () => void {
	switch (detectPlatform()) {
		case "telegram":
			return attachTelegram()
		case "android":
		case "ios":
			return attachNative()
		case "browser":
			return attachVisibility()
	}
}

export function onAppActiveChange(callback: Listener): () => void {
	listeners.add(callback)
	if (listeners.size === 1) {
		detach = attachPlatform()
	}

	return () => {
		if (!listeners.delete(callback)) return
		if (listeners.size === 0) {
			detach?.()
			detach = null
		}
	}
}

export function onAppActive(callback: () => void): () => void {
	return onAppActiveChange((isActive) => {
		if (isActive) {
			callback()
		}
	})
}

export function isAppActive(): boolean {
	switch (detectPlatform()) {
		case "telegram": {
			const miniAppActive = isMiniAppActive()
			if (miniAppActive === null) return document.visibilityState !== "hidden"
			return miniAppActive && document.visibilityState !== "hidden"
		}
		case "android":
		case "ios":
			return nativeActive ?? document.visibilityState !== "hidden"
		case "browser":
			return document.visibilityState !== "hidden"
	}
}
